"use client";

import Link from "next/link";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import useResizeMeasure from "@/hooks/useResizeMeasure";
import { useBannerContext } from "@core/helpers/client/contexts/BannerContext";
import ImageComponent from "@core/helpers/client/components/image";
import BannerSkeleton from "./_bannerSkeleton";
import { useInitialBanners } from "@/contexts/InitialBanners";
import { useServerDevice } from "@/contexts/ServerDevice";

// ارتفاع هر نمونه‌ی بنر؛ هدر مجله دو نمونه (موبایل/دسکتاپ) دارد که یکی‌شان hidden و صفر است.
const banner_heights = new Map();

const apply_banner_height = () => {
  const height = Math.max(0, ...banner_heights.values());
  document.documentElement.style.setProperty("--banner-height", `${height}px`);
};

// بنر داخل جریان هدر fixed است و هدر را پایین می‌برد؛ صفحه‌ها فاصله‌ی ثابت خودشان از هدر را
// دارند و فقط خودِ بنر را نمی‌شناسند، پس ارتفاعش در --banner-height به padding بالای main می‌رسد.
// initial_banners: بنرهای سرور برای مسیر لود اولیه؛ بر جواب BannerProvider مقدم‌اند (توضیح پایین‌تر، کنار `items`).
export const should_show_banner = ({ banner_data, banner_loading, initial_count, initial_banners }) =>
  initial_banners?.length > 0 || banner_data?.length > 0 || (initial_count > 0 && banner_loading !== false);

export default function BannerSection({ initial_count = 0 }) {
  const { banner_data, banner_loading } = useBannerContext();
  const initial_banners = useInitialBanners();
  // تا matchMedia اجرا شود حدس سرور از User-Agent؛ وگرنه گوشی اول تصویر دسکتاپ را می‌گرفت و بعد عوض می‌کرد.
  // تبلت و agent ناشناس مثل قبل با تصویر دسکتاپ شروع می‌کنند.
  const server_device = useServerDevice();
  const [isMobile, setIsMobile] = useState(server_device === "mobile");
  const rootRef = useRef(null);
  const id = useId();

  // همان شرط قبلی (innerWidth <= 768) با matchMedia: خواندن innerWidth بعد از تغییر DOM layout اجباری می‌ساخت
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const query = window.matchMedia("(max-width: 768px)");
    const checkWidth = () => setIsMobile(query.matches);

    checkWidth();
    query.addEventListener("change", checkWidth);

    return () => query.removeEventListener("change", checkWidth);
  }, []);

  // ارتفاع فقط در ResizeObserver خوانده می‌شود (useResizeMeasure)؛ خواندنش در useLayoutEffect layout اجباری می‌ساخت
  useResizeMeasure(
    () => [rootRef.current],
    () => {
      if (!rootRef.current) return;
      const height = rootRef.current.getBoundingClientRect().height;
      return () => {
        banner_heights.set(id, height);
        apply_banner_height();
      };
    },
    [id]
  );

  // با unmount سهم این نمونه از --banner-height برداشته می‌شود (بدون خواندن layout)
  useLayoutEffect(
    () => () => {
      banner_heights.delete(id);
      apply_banner_height();
    },
    [id]
  );

  // در مسیر لود اولیه بنرهای سرور مقدم‌اند، حتی بعد از رسیدن داده‌ی BannerProvider: هسته پیشوند زبان را با
  // `pathname.replace("/fa", "")` برمی‌دارد که وسط مسیر هم می‌خورد (/blogs/category/fashion-news/ →
  // /blogs/category/shion-news/ → found:false)، پس بنر بعد از لود حذف می‌شد و کل صفحه ۶۴px می‌پرید (CLS 0.078).
  // بعد از ناوبری کلاینتی initial_banners null است و داده‌ی هسته استفاده می‌شود.
  const items = initial_banners?.length > 0
    ? initial_banners
    : banner_data?.length > 0 ? banner_data : null;

  return (
    <div ref={rootRef} className="relative w-full">
      {items
        ? items.map((item, index) => (
          <Link
            href={item?.link?.url || ""}
            key={index}
            // بنر بدون عنوان، لینک بی‌نام می‌شد (Lighthouse: link-name)
            aria-label={item?.title || "بنر تبلیغاتی"}
            className="h-12 w-auto relative block">
            <ImageComponent
              src={
                isMobile ? item?.mobile_media?.url : item?.desktop_media?.url
              }
              alt={item?.title}
              fill
              // "preload" و نه "eager": eager همیشه fetchpriority=low می‌گذاشت. در صفحه‌هایی که تصویر بزرگ‌تری
              // ندارند (جستجو، دسته‌های مجله) خود بنر عنصر LCP است و با اولویت پایین چند ثانیه دیر می‌رسید.
              // preload اولویت را بالا نمی‌برد؛ مرورگر آن را وقتی در viewport دید بالا می‌برد. فایل‌ها کوچک‌اند
              // (GIF موبایل ~۱۷KB، دسکتاپ ~۴۸KB) و با تصویر hero صفحه رقابت جدی ندارند.
              priority_level="preload"
              sizes="100vw"
              className="object-cover"
              style={{ width: "100%", height: "100%" }}
            />
          </Link>
        ))
        : banner_loading !== false && <BannerSkeleton count={initial_count} />}
    </div>
  );
}
